'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

const ROTATE_INTERVAL = 4000;
const CDN = 'https://syllabus-cdn.pengi.ai/assets';

const LOGO = {
  khan: '/assets/site-logos/khan-academy.svg',
  ixl: '/assets/site-logos/ixl.svg',
  phet: '/assets/site-logos/phet.svg',
  scratch: '/assets/site-logos/scratch.svg',
} as const;

const SITES = [
  { key: 'khan', name: 'Khan Academy', logo: LOGO.khan, image: `${CDN}/extension-khan.png` },
  { key: 'ixl', name: 'IXL', logo: LOGO.ixl, image: `${CDN}/extension-ixl.png` },
  { key: 'phet', name: 'PhET Simulations', logo: LOGO.phet, image: `${CDN}/extension-phet.png` },
  { key: 'scratch', name: 'Scratch', logo: LOGO.scratch, image: `${CDN}/extension-scratch.png` },
] as const;

export type BrowserMockupSite = (typeof SITES)[number]['key'];

interface BrowserMockupProps {
  /** Which site to start on (when user came from a site-specific campaign). Defaults to the first. */
  defaultSite?: BrowserMockupSite;
  /** Pause auto-rotation (useful when this block is the secondary demo and shouldn't grab attention). */
  autoRotate?: boolean;
}

export default function BrowserMockup({
  defaultSite,
  autoRotate = true,
}: BrowserMockupProps) {
  const initialIndex = defaultSite
    ? Math.max(
        0,
        SITES.findIndex((s) => s.key === defaultSite),
      )
    : 0;
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const directionRef = useRef(1);

  const goTo = useCallback(
    (idx: number) => {
      if (idx === activeIndex) return;
      directionRef.current = idx > activeIndex ? 1 : -1;
      setActiveIndex(idx);
    },
    [activeIndex],
  );

  useEffect(() => {
    if (!autoRotate) return undefined;
    const timer = setInterval(() => {
      directionRef.current = 1;
      setActiveIndex((prev) => (prev + 1) % SITES.length);
    }, ROTATE_INTERVAL);
    return () => clearInterval(timer);
  }, [autoRotate]);

  const site = SITES[activeIndex];

  return (
    <div className="mx-auto max-w-[1100px]">
      {/* ── Image ── */}
      <div className="mx-auto overflow-hidden rounded-2xl" style={{ width: '80%' }}>
        <AnimatePresence mode="wait" custom={directionRef.current}>
          <motion.img
            key={activeIndex}
            src={site.image}
            alt={site.name}
            custom={directionRef.current}
            initial={{ opacity: 0, x: directionRef.current * 40, scale: 0.98 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: directionRef.current * -40, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
            className="w-full object-cover"
          />
        </AnimatePresence>
      </div>

      {/* ── Site selector tabs ── */}
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        {SITES.map((s, i) => (
          <button
            key={s.key}
            onClick={() => goTo(i)}
            className={`group inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-all duration-200 ${
              i === activeIndex
                ? 'border-[#C5EDB8] bg-white shadow-sm'
                : 'border-transparent bg-transparent hover:bg-white/60'
            }`}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={s.logo} alt={s.name} className="h-5 w-5 rounded-sm object-contain" />
            <span
              className={`display-text-semi hidden transition-colors sm:inline ${
                i === activeIndex ? 'text-[#1F2A44]' : 'text-[#8A96A8]'
              }`}
            >
              {s.name}
            </span>
            {i === activeIndex && autoRotate && (
              <span className="relative ml-1 h-1 w-8 overflow-hidden rounded-full bg-[#E3E8F2]">
                <span
                  className="absolute inset-y-0 left-0 rounded-full bg-[#42C52D]"
                  style={{ animation: `progress-fill ${ROTATE_INTERVAL}ms linear` }}
                />
              </span>
            )}
          </button>
        ))}
      </div>

      <style jsx>{`
        @keyframes progress-fill {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </div>
  );
}
